// Shared layout model for the digitised VQF Form A / Form K, per Jaybee's review guidance
// (Xavier Palma, 16 Jul 2026): full VQF form titles, a Contracting Partner block first
// (sourced from the verified company record, not typed by the signer), then the declaration
// fields in the prescribed order, closing with the confirmation evidence (IP + date + OTP).
// Consumed by the declaration modals (MLRO dashboard + Jaybee portal) and the Form
// Confirmation PDF included in the case-file ZIP export (Art. 63 VQF Regulations).

export const VQF_FORM_TITLES = {
  K: "Form K — Establishing of the controlling person of operating legal entities and partnerships both not quoted on the stock exchange",
  A: "Form A — Establishing of the beneficial owner of the assets",
};

// Canonical presentation order. Sumsub questionnaire item ids → display labels.
// Form K may require MORE THAN ONE controlling person to be listed (under the 25% test
// there can be up to four), and Form A likewise where assets have several beneficial
// owners. Person 1 uses the original unsuffixed ids; persons 2-4 use a numeric suffix.
//
// Sumsub appears to truncate dashboard-generated item ids at 20 characters (every
// generated id in the existing form is exactly 20: actualAddressOfDomic,
// fiduciaryQuestionLab, formKEstablishingOfT). "actualAddressOfDomic2" would be 21 and
// could truncate back into a collision with person 1, so each field accepts several
// candidate ids and uses whichever is actually present. Blocks whose fields are absent
// simply do not render, so this is inert until the fields are added in Sumsub.
const PERSON_FIELDS = [
  { base: "lastNames", label: "Last name(s)", alt: (n) => [`lastNames${n}`, `lastName${n}`] },
  { base: "firstNames", label: "First name(s)", alt: (n) => [`firstNames${n}`, `firstName${n}`] },
  { base: "dateOfBirth", label: "Date of birth", alt: (n) => [`dateOfBirth${n}`, `dob${n}`] },
  { base: "nationality", label: "Nationality", alt: (n) => [`nationality${n}`] },
  { base: "actualAddressOfDomic", label: "Domicile", alt: (n) => [`actualAddressOfDomic${n}`, `domicile${n}`, `addressOfDomicile${n}`] },
];

const personBlock = (n) => PERSON_FIELDS.map(({ base, label, alt }) => ({
  ids: n === 1 ? [base] : alt(n),
  label: n === 1 ? label : `${label} — person ${n}`,
}));

const FIELD_ORDER = [
  { ids: ["question1"], label: "Declaration basis" },
  ...personBlock(1),
  ...personBlock(2),
  ...personBlock(3),
  ...personBlock(4),
  { ids: ["fiduciaryQuestionLab"], label: "Fiduciary holding of assets (third-party beneficial owner)" },
  { ids: ["informationUpdateObl"], label: "Information Update Obligation" },
  { ids: ["pleaseConfirmThatYou"], label: "False declaration warning (Art. 251 SCC)" },
];

// Ids that are structural (section wrappers / entity-type context / the person-count
// selector that drives the conditional blocks) rather than declaration fields.
const NON_FIELD_IDS = new Set([
  "formKEstablishingOfT", "formADeclarationOfId", "formAOfTheBeneficial", "pleaseChooseLegalEnt",
  "declaredPersonCount", "howManyControllingP", "howManyPersonsAreBe",
]);

const itemValue = (item, valueLabel) => {
  if (!item) return null;
  if (item.value !== undefined && item.value !== null && item.value !== "") return valueLabel(item.value);
  if (Array.isArray(item.values) && item.values.length) return item.values.map(valueLabel).join(", ");
  return null;
};

// record: sumsub_form_requests row · questionnaire: {sections:{id:{items:{id:{value|values}}}}}
// session / report: onboarding session + report JSONB · fieldLabel/valueLabel: caller's label maps
export function buildVqfFormModel({ record, questionnaire, session, report, fieldLabel, valueLabel }) {
  const cd = (report && report.companyDetails) || {};
  const items = {};
  Object.values((questionnaire && questionnaire.sections) || {}).forEach((section) => {
    Object.entries((section && section.items) || {}).forEach(([id, item]) => { items[id] = item; });
  });

  const fields = [];
  const used = new Set(NON_FIELD_IDS);
  FIELD_ORDER.forEach(({ ids, label }) => {
    ids.forEach((id) => used.add(id));
    // Use the first candidate id actually present in the questionnaire.
    for (const id of ids) {
      const v = itemValue(items[id], valueLabel);
      if (v !== null) { fields.push({ id, label, value: v }); break; }
    }
  });
  // Any additional questionnaire fields not in the canonical order render after, unlabelled loss-free.
  Object.entries(items).forEach(([id, item]) => {
    if (used.has(id)) return;
    const v = itemValue(item, valueLabel);
    if (v !== null) fields.push({ id, label: fieldLabel(id), value: v });
  });

  const fidItem = items["fiduciaryQuestionLab"];
  const fidRaw = fidItem && (fidItem.value || (Array.isArray(fidItem.values) ? fidItem.values[0] : null));
  const fiduciaryYes = String(fidRaw || "").toLowerCase() === "yes";

  return {
    formType: record && record.form_type,
    title: VQF_FORM_TITLES[record && record.form_type] || `Form ${record && record.form_type}`,
    signerName: (record && record.signer_name) || "",
    contractingPartner: {
      legalName: cd.legalName || (session && session.company_name) || "",
      address: cd.registeredAddress || "",
      country: cd.jurisdiction || "",
      registrationNumber: cd.registrationNumber || "",
    },
    fields,
    fiduciaryYes,
    confirmation: {
      at: record && record.confirmed_at,
      ip: record && record.confirmation_ip,
      actionId: record && record.applicant_action_id,
    },
  };
}
